/**
 * @license
 * @copyright Copyright Motili Inc., 2025 All Rights Reserved
 */

import {
    configureStore,
    combineReducers,
    UnknownAction,
} from '@reduxjs/toolkit';
import {
    persistReducer,
    FLUSH,
    REHYDRATE,
    PAUSE,
    PERSIST,
    PURGE,
    REGISTER,
} from 'redux-persist';
import storage from 'redux-persist/lib/storage';

import contractor from 'admin/stores/redux/ducks/AdminContractorRegistration';
import recentlyViewed from 'admin/stores/redux/AdminRecentlyViewedStore';
import workOrder from 'admin/stores/redux/ducks/AdminWorkOrderCreation';
import adminFinanceInvoicesListFilter, {
    actions as adminFinanceInvoicesListFilterActions,
} from 'admin/stores/redux/AdminFinanceInvoicesListFilter';
import adminFinanceInvoiceCreateFilter from 'admin/stores/redux/AdminFinanceInvoiceCreateFilter';
import adminFinanceSyncTaskListFilter from 'admin/stores/redux/AdminFinanceSyncTaskListFilter';
import adminFinanceTransactionTaxHistoryListFilter from 'admin/stores/redux/AdminFinanceTransactionTaxHistoryListFilter';
import businessChannel from 'admin/stores/redux/AdminBusinessChannelStore';

import { tourListenerMiddleware, tourReducer as tour } from 'common/guide-tour';
import contractorAvailableJobs from 'common/stores/redux/ContractorAvailableJobs/ContractorAvailableJobs';
import requirements from 'common/stores/RequirementsSpecsStore';
import distributionCentersAndInventory from 'common/stores/redux/DistributionCentersAndInventory/DistributionCentersAndInventory';
import contractorServiceMapView from 'common/stores/redux/ContractorServiceMap/ContractorServiceMap';
import authentication from 'common/stores/redux/Authentication';
import dataGridState from 'common/stores/redux/DataGrid/DataGrid';
import metaStore from 'common/stores/redux/MetaStore/MetaStore';
import truck from 'common/stores/redux/Truck';
import project from 'common/stores/redux/Project';

// ===== Admin ===== //
import adminWorkOrder from 'admin/stores/redux/AdminWorkOrder';
import adminAccount from 'admin/stores/redux/AdminAccount';
import adminContractorCompany from 'admin/stores/redux/AdminContractorCompany';
import adminBusinessChannelFilter from 'admin/stores/redux/AdminBusinessChannelFilter';
import adminClient from 'admin/stores/redux/AdminClient';
import adminClientFilter from 'admin/stores/redux/AdminClientFilter';
import adminClientInvoiceFilter from 'admin/stores/redux/AdminClientInvoiceFilter';
import adminClientManagedPricingDetail from 'admin/stores/redux/AdminClientManagedPricingDetail';
import adminDraft from 'admin/stores/redux/AdminDraft';
import adminDraftFilter from 'admin/stores/redux/AdminDraftFilter';
import adminOrder from 'admin/stores/redux/AdminOrder';
import adminManagedVendor from 'admin/stores/redux/AdminManagedVendor';
import adminOrderCreation from 'admin/stores/redux/AdminOrderCreation';
import adminQuote from 'admin/stores/redux/AdminQuote';
import adminOrderQuoteCreation from 'admin/stores/redux/AdminOrderQuoteCreation';
import adminProduct from 'admin/stores/redux/AdminProduct';
import adminProperty from 'admin/stores/redux/AdminProperty';
import adminTemplate from 'admin/stores/redux/AdminTemplate';
import adminModifyOrder from 'admin/views/orders/order/AdminOrderModifyContainer/modifyOrderSlice';
import adminOrderQuoteClone from 'admin/stores/redux/AdminOrderQuoteClone';
import adminOrderClone from 'admin/stores/redux/AdminOrderClone';

// ===== Client ===== //
import clientCompany from 'client/stores/redux/ClientCompany';
import clientOrderDetails from 'client/stores/redux/ClientOrder';
import clientOrderCreation from 'client/stores/redux/ClientOrderCreation';
import clientProperty from 'client/stores/redux/ClientProperty';
import clientDashboard from 'common/stores/redux/Dashboard/ClientDashboardSlice';

// ===== Contractor ===== //
import contractorOrderCreation from 'contractor/stores/redux/ContractorOrderCreation';
import contractorQuote from 'contractor/stores/redux/ContractorQuote';
import contractorWorkOrder from 'contractor/stores/redux/ContractorWorkOrder';
import contractorOrderDetails from 'contractor/stores/redux/ContractorOrderDetails';
import contractorCompanyDetails from 'contractor/stores/redux/ContractorCompanyDetails';
import contractorDashboard from 'common/stores/redux/Dashboard/ContractorDashboardSlice';
import featuresFlag from 'common/stores/redux/FeaturesFlag';

import QueryStoreApi from './baseApi';

const appReducer = combineReducers({
    authentication,
    contractor,
    recentlyViewed,
    workOrder,
    metaStore,
    dataGridState,
    [QueryStoreApi.reducerPath]: QueryStoreApi.reducer,
    requirements,
    businessChannel,
    adminAccount,
    adminBusinessChannelFilter,
    adminClient,
    adminClientFilter,
    adminClientInvoiceFilter,
    adminContractorCompany,
    adminDraft,
    adminDraftFilter,
    adminClientManagedPricingDetail,
    adminFinanceInvoicesListFilter,
    adminFinanceInvoiceCreateFilter,
    adminFinanceSyncTaskListFilter,
    distributionCentersAndInventory,
    adminFinanceTransactionTaxHistoryListFilter,
    adminOrder,
    adminManagedVendor,
    adminOrderCreation,
    adminOrderQuoteCreation,
    adminProduct,
    adminProperty,
    adminWorkOrder,
    adminQuote,
    adminTemplate,
    contractorServiceMapView,
    contractorAvailableJobs,
    tour,
    project,
    clientCompany,
    clientDashboard,
    clientOrderDetails,
    clientOrderCreation,
    clientProperty,
    contractorCompanyDetails,
    contractorDashboard,
    contractorOrderCreation,
    contractorOrderDetails,
    contractorQuote,
    contractorWorkOrder,
    truck,
    featuresFlag,
    adminModifyOrder,
    adminOrderQuoteClone,
    adminOrderClone,
});

const persistConfig = {
    key: 'root',
    version: 1,
    // @ts-expect-error: Vite CommonJS interop issue with redux-persist
    storage: storage.default || storage,
    whitelist: [
        'authentication',
        'recentlyViewed',
        'metaStore',
        'dataGridState',
        'adminBusinessChannelFilter',
        'adminClientFilter',
        'adminClientInvoiceFilter',
        'adminDraftFilter',
        'adminFinanceInvoicesListFilter',
        'adminFinanceInvoiceCreateFilter',
        'adminFinanceSyncTaskListFilter',
        'adminFinanceTransactionTaxHistoryListFilter',
        'contractorServiceMapView',
        'contractorAvailableJobs',
        'featuresFlag',
    ],
};

const rootReducer = (
    state: ReturnType<typeof appReducer> | undefined,
    action: { type: string }
) => {
    if (action.type === 'RESET_ALL_STORES') {
        return appReducer(undefined, action);
    }
    return appReducer(state, action);
};

const persistedReducer = persistReducer<RootReducer, UnknownAction>(
    persistConfig,
    rootReducer
);

const store = configureStore({
    reducer: persistedReducer,
    middleware: getDefaultMiddleware =>
        getDefaultMiddleware({
            serializableCheck: {
                ignoredActions: [
                    FLUSH,
                    REHYDRATE,
                    PAUSE,
                    PERSIST,
                    PURGE,
                    REGISTER,
                ],
            },
        })
            .concat(QueryStoreApi.middleware)
            .prepend(tourListenerMiddleware.middleware),
});

export const initStoreAfterLogin = () => {
    store.dispatch(adminFinanceInvoicesListFilterActions.init());
};

export const resetAllStores = () => {
    store.dispatch({ type: 'RESET_ALL_STORES' });
};

export type RootState = ReturnType<typeof store.getState>;
export type RootReducer = ReturnType<typeof rootReducer>;
export type AppDispatch = typeof store.dispatch;

export default store;
